import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get, noop } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { matchPath } from 'react-router-dom';
import { AppIcon } from '@folio/stripes-core';
import {
  Button,
  Icon,
  Loading,
  Layout,
  MultiColumnList,
  Pane,
  PaneMenu,
  Paneset,
  SearchField,
  MCLPagingTypes,
} from '@folio/stripes/components';
import {
  SearchAndSortQuery,
  SearchAndSortNoResultsMessage as NoResultsMessage,
  ExpandFilterPaneButton,
  CollapseFilterPaneButton,
} from '@folio/stripes/smart-components';
import { getLocalizedValue } from '../../../../helpers/localizationUtils';
import Filters from './searchAndFilters/Filters';
import ReportDetails from '../view/ReportDetails';

const searchableIndexes = [
  { value: '', label: 'All fields' },
  { value: 'name', label: 'Name' },
  { value: 'builderType', label: 'Builder type' },
];

function ReportsList({
  source,
  initialSearch,
  onNeedMoreData,
  queryGetter,
  querySetter,
  resources,
  mutator,
  history,
  location,
}) {
  const intl = useIntl();
  const { locale } = intl;
  const [filterPaneIsVisible, setFilterPaneIsVisible] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);

  const reports = get(resources, 'reports.records', []);
  const categories = get(resources, 'categories.records', []);
  const reportTypes = get(resources, 'reportTypes.records', []);
  const count = (source && source.totalCount() != null)
    ? source.totalCount()
    : get(resources, 'reports.other.totalRecords', reports.length);
  const query = queryGetter ? queryGetter() || {} : {};
  const sortOrder = query.sort || '';

  // Detect selected report from URL
  const urlMeta = matchPath(location.pathname, {
    path: '/reports/reports-list/:reportId/view',
  });
  const currentReportId = urlMeta?.params?.reportId || null;
  const openDetailsPane = !!currentReportId;

  // When URL has reportId but selectedReport is null, find it from the list
  useEffect(() => {
    if (currentReportId && reports.length > 0 && !selectedReport) {
      const found = reports.find((r) => r.id === currentReportId);
      if (found) setSelectedReport(found);
    }
    if (!currentReportId) setSelectedReport(null);
  }, [currentReportId, reports]);

  const toggleFilterPane = () => setFilterPaneIsVisible((prev) => !prev);

  const onRowClick = (e, item) => {
    history.push({
      pathname: `/reports/reports-list/${item.id}/view`,
      search: location.search,
    });
    setSelectedReport(item);
  };

  const onCloseDetailPane = () => {
    history.push({
      pathname: '/reports/reports-list',
      search: location.search,
    });
    setSelectedReport(null);
  };

  const isSelected = ({ item }) => item.id === currentReportId;

  const columnMapping = {
    name: (
      <FormattedMessage
        id="ui-reports.panes.reportsList.columns.reportName"
        defaultMessage="Report name"
      />
    ),
    desc: (
      <FormattedMessage
        id="ui-reports.panes.reportsList.columns.reportDesc"
        defaultMessage="Description"
      />
    ),
    builderType: (
      <FormattedMessage
        id="ui-reports.panes.reportsList.columns.builderType"
        defaultMessage="Builder"
      />
    ),
    status: (
      <FormattedMessage
        id="ui-reports.panes.reportsList.columns.status"
        defaultMessage="Status"
      />
    ),
    visibility: (
      <FormattedMessage
        id="ui-reports.panes.reportsList.columns.privacyType"
        defaultMessage="Visibility"
      />
    ),
    categoryId: (
      <FormattedMessage
        id="ui-reports.filters.category"
        defaultMessage="Category"
      />
    ),
    reportType: (
      <FormattedMessage
        id="ui-reports.filters.reportType"
        defaultMessage="Report Type"
      />
    ),
  };

  const formatter = {
    name: (item) => getLocalizedValue(item.name, locale),
    desc: (item) => getLocalizedValue(item.desc, locale),
    builderType: (item) =>
      intl.formatMessage({
        id: `ui-reports.filters.builderType.${item.builderType}`,
        defaultMessage: item.builderType,
      }),
    status: (item) => {
      const statusColors = {
        published: { bg: '#d1e7dd', color: '#0a3622', border: '#a3cfbb' },
        draft:     { bg: '#fff3cd', color: '#664d03', border: '#ffecb5' },
        archived:  { bg: '#e2e3e5', color: '#41464b', border: '#c4c8cb' },
        scheduled: { bg: '#cfe2ff', color: '#084298', border: '#b6d4fe' },
      };
      const s = statusColors[item.status] || statusColors.draft;
      return (
        <span style={{
          display: 'inline-block',
          padding: '0.15em 0.5em',
          borderRadius: '0.25rem',
          fontSize: '0.78rem',
          fontWeight: '600',
          background: s.bg,
          color: s.color,
          border: `1px solid ${s.border}`,
        }}>
          {intl.formatMessage({
            id: `ui-reports.filters.status.${item.status}`,
            defaultMessage: item.status,
          })}
        </span>
      );
    },
    visibility: (item) => {
      const visibilityColors = {
        public:     { bg: '#e8f5e9', color: '#1b5e20', border: '#4caf50' },
        private:    { bg: '#fff3e0', color: '#e65100', border: '#ff9800' },
        restricted: { bg: '#e3f2fd', color: '#0d47a1', border: '#2196f3' },
      };
      const vis = item.permissions?.visibility || 'public';
      const s = visibilityColors[vis] || visibilityColors.public;
      return (
        <span style={{
          display: 'inline-block',
          padding: '0.15em 0.5em',
          borderRadius: '0.25rem',
          fontSize: '0.78rem',
          fontWeight: '600',
          backgroundColor: s.bg,
          color: s.color,
          border: `1px solid ${s.border}`,
          whiteSpace: 'nowrap',
        }}>
          {intl.formatMessage({
            id: `ui-reports.filters.privacyType.${vis}`,
            defaultMessage: vis,
          })}
        </span>
      );
    },
    categoryId: (item) => {
      const cat = categories.find((c) => c.id === item.categoryId);
      if (!cat) return item.categoryId || '';
      return intl.formatMessage({
        id: `ui-reports.categories.name.${cat.name}`,
        defaultMessage: cat.name || '',
      });
    },
    reportType: (item) => {
      const rt = reportTypes.find((r) => r.id === item.reportType);
      if (!rt) return item.reportType || '';
      return intl.formatMessage({
        id: `ui-reports.reportTypes.name.${rt.code}`,
        defaultMessage: rt.name || '',
      });
    },
  };

  const resultsStatusMessage = source ? (
    <NoResultsMessage
      source={source}
      searchTerm={query.query || ''}
      filterPaneIsVisible
      toggleFilterPane={noop}
    />
  ) : (
    'no source yet'
  );

  const activeFilterCount = query.filters
    ? query.filters.split(',').filter(Boolean).length
    : 0;

  const reportsHasLoaded = get(resources, 'reports.hasLoaded', false);
  const sourceLoaded = source ? source.loaded() : false;
  const isLoaded = sourceLoaded || (reportsHasLoaded && reports.length > 0);

  let resultPaneSub;
  if (!isLoaded) {
    resultPaneSub = (
      <FormattedMessage id="stripes-smart-components.searchCriteria" />
    );
  } else if (query.query || activeFilterCount > 0) {
    const searchDesc = query.query
      ? intl.formatMessage(
          {
            id: 'ui-reports.search.resultsForTerm',
            defaultMessage: 'Results for "{term}"',
          },
          { term: query.query },
        )
      : null;
    const filterDesc =
      activeFilterCount > 0
        ? intl.formatMessage(
            {
              id: 'ui-reports.search.activeFiltersCount',
              defaultMessage: '{count} filter(s) applied',
            },
            { count: activeFilterCount },
          )
        : null;
    const parts = [searchDesc, filterDesc].filter(Boolean).join(' — ');
    resultPaneSub = (
      <>
        <FormattedMessage
          id="stripes-smart-components.searchResultsCountHeader"
          values={{ count }}
        />
        {parts && (
          <span
            style={{
              marginInlineStart: '0.5em',
              color: 'var(--color-text-secondary, #666)',
            }}
          >
            ({parts})
          </span>
        )}
      </>
    );
  } else {
    resultPaneSub = (
      <FormattedMessage
        id="stripes-smart-components.searchResultsCountHeader"
        values={{ count }}
      />
    );
  }

  return (
    <SearchAndSortQuery
      querySetter={querySetter}
      queryGetter={queryGetter}
      initialSearch={initialSearch}
      initialSearchState={{ query: '' }}
      location={location}
    >
      {({
        searchValue,
        getSearchHandlers,
        onSubmitSearch,
        onSort,
        getFilterHandlers,
        activeFilters,
        filterChanged,
        searchChanged,
        resetAll,
      }) => (
        <Paneset isRoot>
          {/* Filter Pane */}
          {filterPaneIsVisible && (
            <Pane
              defaultWidth="22%"
              paneTitle={
                <FormattedMessage
                  id="ui-reports.panes.filtersPane.title"
                  defaultMessage="Search & Filter"
                />
              }
              lastMenu={
                <PaneMenu>
                  <CollapseFilterPaneButton onClick={toggleFilterPane} />
                </PaneMenu>
              }
            >
              <form onSubmit={onSubmitSearch}>
                <SearchField
                  aria-label={intl.formatMessage({
                    id: 'ui-reports.panes.reportsList.title',
                    defaultMessage: 'Reports',
                  })}
                  autoFocus
                  autoComplete="off"
                  name="query"
                  id="input-reports-search"
                  onChange={(e) => {
                    if (e.target.value) {
                      getSearchHandlers().query(e);
                    } else {
                      getSearchHandlers().reset();
                    }
                  }}
                  value={searchValue.query}
                  marginBottom0
                  searchableIndexes={searchableIndexes.map((i) => ({
                    ...i,
                    label: intl.formatMessage({
                      id: `ui-reports.index.${i.value || 'all'}`,
                      defaultMessage: i.label,
                    }),
                  }))}
                  selectedIndex={searchValue.qindex}
                  onChangeIndex={getSearchHandlers().query}
                />
                <div style={{ marginBottom: '0.5rem' }} />
                <Button
                  id="submit-reports-search"
                  type="submit"
                  buttonStyle="primary"
                  fullWidth
                  marginBottom0
                  disabled={!searchValue.query}
                >
                  <FormattedMessage
                    id="stripes-smart-components.search"
                    defaultMessage="Search"
                  />
                </Button>
                <div style={{ marginBottom: '0.5rem' }} />
                <Button
                  buttonStyle="none"
                  id="clickable-reset-all-reports"
                  disabled={!(filterChanged || searchChanged)}
                  fullWidth
                  onClick={resetAll}
                >
                  <Icon icon="times-circle-solid">
                    <FormattedMessage
                      id="stripes-smart-components.resetAll"
                      defaultMessage="Reset all"
                    />
                  </Icon>
                </Button>
                <div style={{ marginBottom: '1rem' }} />
                <Filters
                  activeFilters={activeFilters.state}
                  resources={resources}
                  onChangeHandlers={getFilterHandlers()}
                  resultOffset={mutator.resultOffset}
                />
              </form>
            </Pane>
          )}

          {/* Results Pane */}
          <Pane
            id="pane-reports-list"
            appIcon={<AppIcon app="reports" />}
            defaultWidth="fill"
            padContent={false}
            noOverflow
            paneTitle={
              <FormattedMessage
                id="ui-reports.panes.reportsList.title"
                defaultMessage="Reports List"
              />
            }
            paneSub={resultPaneSub}
            firstMenu={
              !filterPaneIsVisible ? (
                <PaneMenu>
                  <ExpandFilterPaneButton
                    filterCount={activeFilterCount}
                    onClick={toggleFilterPane}
                  />
                </PaneMenu>
              ) : null
            }
          >
            <MultiColumnList
              id="reports-list-mcl"
              interactive
              visibleColumns={[
                'name',
                'desc',
                'builderType',
                'status',
                'visibility',
                'categoryId',
                'reportType',
              ]}
              columnWidths={{
                name: '20%',
                desc: '25%',
                builderType: '10%',
                status: '10%',
                visibility: '10%',
                categoryId: '13%',
                reportType: '12%',
              }}
              contentData={reports}
              totalCount={count}
              columnMapping={columnMapping}
              formatter={formatter}
              onNeedMoreData={onNeedMoreData}
              onHeaderClick={onSort}
              sortOrder={sortOrder.replace(/^-/, '').replace(/,.*/, '')}
              sortDirection={
                sortOrder.startsWith('-') ? 'descending' : 'ascending'
              }
              isEmptyMessage={resultsStatusMessage}
              isSelected={isSelected}
              onRowClick={onRowClick}
              autosize
              hasMargin
              pageAmount={30}
              pagingType={MCLPagingTypes.PREV_NEXT}
            />
          </Pane>

          {/* Details Pane */}
          {openDetailsPane && selectedReport ? (
            <ReportDetails
              open={openDetailsPane}
              onClose={onCloseDetailPane}
              selectedRow={selectedReport}
              currentReportId={currentReportId}
              reportsList={reports}
              mutator={mutator}
              resources={resources}
              history={history}
              location={location}
            />
          ) : openDetailsPane ? (
            <Pane
              id="report-details-loading-pane"
              defaultWidth="40%"
              paneTitle=" "
              dismissible
              onClose={onCloseDetailPane}
            >
              <Layout className="centered full" style={{ height: '8rem' }}>
                <Loading size="xlarge" />
              </Layout>
            </Pane>
          ) : null}
        </Paneset>
      )}
    </SearchAndSortQuery>
  );
}

ReportsList.propTypes = {
  source: PropTypes.object,
  initialSearch: PropTypes.string,
  onNeedMoreData: PropTypes.func.isRequired,
  queryGetter: PropTypes.func,
  querySetter: PropTypes.func,
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.object.isRequired,
  resources: PropTypes.shape({
    reports: PropTypes.object,
    categories: PropTypes.object,
    reportTypes: PropTypes.object,
  }).isRequired,
  mutator: PropTypes.shape({
    resultOffset: PropTypes.shape({ replace: PropTypes.func.isRequired }),
    reports: PropTypes.shape({
      DELETE: PropTypes.func.isRequired,
    }),
  }).isRequired,
};

export default ReportsList;
